import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { elementId }: Boundaries["retrieveElementAttachments"]["request"],
): State {
  const stateIndex = state.findIndex(
    (result) => result.elementId === elementId,
  );

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          elementId,
          request: setRequestStateToPending(),
          attachments: [],
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          elementId,
          request: setRequestStateToPending(),
          attachments: [],
        },
      ];
}

function handleSuccess(
  state: State,
  {
    elementId,
    attachments,
  }: Boundaries["retrieveElementAttachments"]["success"],
): State {
  return state.map((item) =>
    item.elementId === elementId
      ? {
          ...item,
          attachments,

          request: setRequestStateToSuccess(),
        }
      : item,
  );
}

function handleFailure(
  state: State,
  elementId: Boundaries["retrieveElementAttachments"]["failure"],
): State {
  return state.map((item) =>
    item.elementId === elementId
      ? { ...item, request: setRequestStateToFailure() }
      : item,
  );
}

function handleAttachDocumentsToElementSucceeded(
  state: State,
  { elementId, documents }: Boundaries["attachDocumentsToElement"]["success"],
): State {
  return state.map((item) =>
    item.elementId === elementId
      ? { ...item, attachments: [...item.attachments, ...documents] }
      : item,
  );
}

function handleRemoveAttachedDocumentSucceeded(
  state: State,
  { elementId, documentId }: Boundaries["removeAttachedDocument"]["success"],
): State {
  return state.map((item) =>
    item.elementId === elementId
      ? {
          ...item,
          attachments: item.attachments.filter(
            (doc) => doc.uuid !== documentId,
          ),
        }
      : item,
  );
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveElementAttachmentsRequested]: handleRequest,
    [events.retrieveElementAttachmentsSucceeded]: handleSuccess,
    [events.retrieveElementAttachmentsFailed]: handleFailure,
    [events.attachDocumentsToElementSucceeded]:
      handleAttachDocumentsToElementSucceeded,
    [events.removeAttachedDocumentSucceeded]:
      handleRemoveAttachedDocumentSucceeded,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
